import UpdateTemplatesView from '@powerednow/app/FieldServices/view/modal/updateTemplates/updateTemplatesView';
import ROLES from '@powerednow/shared/constants/roles';
import { BaseDescriptor } from './baseDescriptor';
import { AuthManager } from '../AuthManager';

declare let Ext: any;
declare let Utils: any;
declare let L: any;
declare let Deft: any;
export default class V9MessageTemplateChange extends BaseDescriptor {
    name = 'V9MessageTemplateChange';

    introducedIn = '9.0.0';

    affectedStore = 'MessageTemplate';

    oldTemplateSettings: object;

    templateTypeMap = {
        APPOINTMENT: { messageType: 11, channelTypeId: 2 },
        PAYMENT: { messageType: 17, channelTypeId: 2 },
        QUOTE: { messageType: 1, channelTypeId: 2 },
        EXPENSE: { messageType: 9, channelTypeId: 2 },
        INVOICE: { messageType: 0, channelTypeId: 2 },
        SIGNATURE: { messageType: 5, channelTypeId: 2 },
        WORKSHEET: { messageType: 3, channelTypeId: 2 },
        CREDITNOTE: { messageType: 4, channelTypeId: 2 },
        PURCHASE_ORDER: { messageType: 6, channelTypeId: 2 },
        SUPPLIER_INVOICE: { messageType: 7, channelTypeId: 2 },
        EXPENSE_CREDITNOTE: { messageType: 10, channelTypeId: 2 },
        INVOICE_ELECTRONIC: { messageType: 0, channelTypeId: 4 },
        CREDITNOTE_ELECTRONIC: { messageType: 4, channelTypeId: 4 },
        SUPPLIER_INVOICE_CREDIT: { messageType: 8, channelTypeId: 2 },
    };

    openTemplateUpdateBtn = null;

    private generateRules() {
        if (!this.rules.length) {
            Object.entries(this.templateTypeMap).forEach(([templateType, { messageType, channelTypeId }]) => {
                const oldSetting = this.oldTemplateSettings[templateType];
                if (oldSetting && oldSetting.body) {
                    this.rules.push(() => {
                        const storedTemplate = Ext.getStore('MessageTemplate').getTerminology(messageType, channelTypeId);
                        const hasNewTemplate = storedTemplate && !storedTemplate.isDefaultTerminology;
                        const { subject, body } = oldSetting;
                        const breakRecord = this.createChangeRecord({
                            messageType,
                            channelTypeId,
                            oldSubject: subject,
                            oldMessage: body,
                        });
                        return {
                            created: hasNewTemplate ? undefined : breakRecord,
                            fixed: hasNewTemplate ? breakRecord : undefined,
                        };
                    });
                }
            });
        }
    }

    async showAlertNotification() {
        const permissionService = Deft.Injector.resolve('permissionService');
        const isEditor = permissionService.canCurrentUserDo(ROLES.PERMISSIONS.CAN_EDIT_EMAIL_TERMINOLOGY);
        const dontShowAgain = !isEditor ? { dontShowAgain: { dialogId: `${this.name}-dismissableDialog` } } : undefined;
        const editorMessage = L(
            'V9MessageTemplateChange.editorAlert',
            `In the latest version of the app, we updated the way we handle custom message templates.<br/>
             Your have to review all message types that you have set up custom templates for.<br/>
             Do you want to do this now?<br/>
             <i style="color: red"><small>Please note: If you do this later, the affected messages will be sent with their new defaults instead of your old custom template!</small></i>`,
        );
        const editorButtons = [
            {
                text: 'No',
                cls: 'button-back',
            }, {
                text: 'Yes',
                cls: 'button-proceed',
            },
        ];
        Ext.Msg.showAsync({
            title: 'Warning',
            ...dontShowAgain,
            message: isEditor ? editorMessage : L(
                'V9MessageTemplateChange.defaultAlert',
                `In the latest update of the app, we updated the way we handle custom message templates.<br/>
                 Until someone in your company updates the templates, the affected message types will be sent with their new defaults`,
            ),
            buttons: isEditor ? editorButtons : [{
                text: 'OK',
                cls: 'button-proceed',
            }],
        }).then(pressedButton => {
            if (pressedButton === 'Yes') {
                this.showTemplateUpdateModal();
            }
            if (pressedButton === 'No') {
                this.createFloatingButton();
            }
            if (pressedButton === 'OK') {
                this.emit('notificationClosed');
            }
        });
    }

    async showTemplateUpdateModal(rerunCheck = false) {
        if (rerunCheck) {
            await this.runCheck(true);
        }
        const breakRecordStore = Ext.getStore('BreakingChange');
        const messageTemplateStore = Ext.getStore('MessageTemplate');
        const existingRecords = breakRecordStore.getRecordsByFilter('originDescriptor', this.name).sort((r1, r2) => r1.get('details').messageType - r2.get('details').messageType);
        if (existingRecords.length) {
            const popup = new UpdateTemplatesView({
                controllerConfig: {
                    itemConfigs: existingRecords.map(breakRecord => {
                        const {
                            channelTypeId, messageType, oldSubject, oldMessage, 
                        } = breakRecord.get('details');
                        const record = messageTemplateStore.getTerminology(messageType, channelTypeId);
                        const editorConfig = {
                            component: 'FieldServices.widgets.email.EmailContentEditorView',
                            creationConfig: {
                                record,
                                isHiddenSaveButton: true,
                                isHiddenDefaultButton: true,
                                title: '',
                            },
                            bodyItemId: '#terminologyFieldset',
                            setOldValuesFn: editor => editor.getViewModel().reset(oldSubject, oldMessage),
                            setDefaultValuesFn: editor => editor.getViewModel().reset(record.get('subject'), record.get('message')),
                            saveFn: editor => {
                                const values = editor.getController().collectValues();
                                messageTemplateStore.add(values);
                                Ext.getStore('MessageTemplate').sync();
                            },
                        };
                        const tooltipConfigs = [
                            { value: oldMessage, attachToId: '#emailBodyId' },
                            { value: oldSubject, attachToId: '#emailSubjectId' },
                        ];
                        return {
                            editorConfig,
                            tooltipConfigs,
                            breakRecord,
                        };
                    }),
                },
                listeners: {
                    templatesUpdated: () => {
                        Utils.showNotifyBox(L('Settings.v9TemplatesUpdated', 'Message templates updated successfully.'));
                        this.createFloatingButton();
                    },
                    modalClose: () => {
                        this.createFloatingButton();
                    },
                },
            });

            Ext.Viewport.add(popup);
            popup.show();
        } else if (this.openTemplateUpdateBtn) {
            this.openTemplateUpdateBtn.destroy();
            Utils.showNotifyBox(L('v9MessageTemplateChange.noRecordsInfo', 'All affected templates updated'), 'notib-desc');
        }
    }

    createFloatingButton() {
        const templateSettingsComponent = Ext.ComponentManager.get('messageContentsEditor');
        templateSettingsComponent?.setTemplateStoreListenerEnabled(false);

        if (Ext.getStore('BreakingChange').getRecordsByFilter('originDescriptor', this.name).length) {
            this.openTemplateUpdateBtn = Ext.create({
                xtype: 'button',
                cls: 'Button-delete-noicon',
                text: 'Update message templates from V8',
                floating: true,
                bottom: '10px',
                right: '10px',
                handler: button => {
                    this.showTemplateUpdateModal(true).then(() => {
                        Ext.ComponentManager.get('messageContentsEditor').setTemplateStoreListenerEnabled(true);
                        button.destroy();
                    });
                },
            });
            if (templateSettingsComponent) {
                templateSettingsComponent.getParent().getParent().add(this.openTemplateUpdateBtn);
            } else {
                Ext.ComponentManager.onAvailable('messageContentsEditor', () => {
                    const renderContainer = Ext.ComponentManager.get('messageContentsEditor').getParent().getParent();
                    renderContainer.add(this.openTemplateUpdateBtn);
                });
            }

            if (!templateSettingsComponent || !templateSettingsComponent.isPainted()) {
                Utils.showNotifyBox(L('V9MessageTemplateChange.buttonInfo', 'You can access the template update interface with the "Update message templates from V8" button on the Message contents tab of the Communications settings page.'), 'notib-desc');
            }
        }
        this.emit('notificationClosed');
    }

    async runCheck(suppressAlert): Promise<void> {
        this.oldTemplateSettings = AuthManager.getCompany().get('email_terminology');
        this.generateRules();
        await super.runCheck(suppressAlert);
    }
}
